import { api } from './client';
import type { ReferencesReport } from './persons';
import type {
  Agreement, AgreementCreateInput, AgreementUpdateInput,
  ClientType, LifecycleStatus, Page, ServiceType, UUID, WriteResult,
} from './types';

// Re-export so existing callers that import LifecycleStatus from this
// module keep working. The canonical definition lives in types.ts (#273).
export type { LifecycleStatus } from './types';

/** P10 (Pedro 2026-10-11): each party of the agreement and the MiFIR identifier it would
 *  give — `value` null = greyed in the picker, with `reason`; `warning` = offered, check it. */
export interface MifirSuggestionOption {
  person_id: UUID;
  display_name: string;
  roles: string[];
  value: string | null;
  reason: string | null;
  warning: string | null;
}
export interface MifirSuggestions { id_type: 'NCI' | 'LEI'; options: MifirSuggestionOption[] }

export interface AgreementsListParams {
  service_type?: ServiceType;
  /** #277 (2026-06-08): multi-status filter via repeated query string.
   *  Backend interprets the list as set membership (UNION across statuses). */
  status?: LifecycleStatus[];
  /** Multi-select (C-132 amendment): repeated query keys via qs(). */
  client_type?: ClientType[];
  q?: string;
  include_deleted?: boolean;
  // #46 Wave 1 (2026-06-12): per-column predicate filters (C-132). Mirrors
  // GET /agreements; the same keys apply on /agreements/export.xlsx so the
  // CSV always matches the screen. Closed-set filters are multi-select.
  code_q?: string;
  mnemonic_q?: string;
  investor_nature?: string[];
  residence?: string[];
  mifir_q?: string;
  iaa_from?: string;
  iaa_to?: string;
  /** Multi-select since C-132 Amendment 3 (repeated query keys via qs()). */
  named_rm_user_id?: UUID[];
  /** #46 follow-up (2026-06-12): rollup-bucket filters mirroring the
   *  PEP / USP dot semantics (worst answered signal wins). Values:
   *  PEP | Related | Nao | NotSet, and Yes | No | NotSet respectively.
   *  Repeated query keys via qs() (array-capable since #277). */
  pep_agg?: string[];
  usp_agg?: string[];
  /** P19(a) (2026-10-10): filter by the strategic allocation (SAA). Multi. */
  strategic_allocation?: string[];
  /** P41: a column key, '-' prefix to reverse (server-side). */
  sort?: string;
  limit?: number;
  offset?: number;
}

function qs(params: object): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    // #277 (2026-06-08): array values expand to repeated query keys —
    // ?status=Active&status=Inactive — to match FastAPI's list parsing.
    if (Array.isArray(v)) {
      for (const item of v) {
        if (item !== undefined && item !== null && item !== '') {
          usp.append(k, String(item));
        }
      }
    } else {
      usp.set(k, String(v));
    }
  }
  return usp.toString() ? `?${usp}` : '';
}

// Writes go through the maker-checker pipeline. The backend returns:
//   200/201 OK     — auto-applied (admin/compliance), live row mutated
//   202 Accepted   — filed PENDING (rm/staff), live row unchanged
// Plus headers X-Change-Request-Id (always populated) and X-Change-Request-Status.
async function writeWithMaker<T>(
  call: () => Promise<{ data: T; status: number; headers: Headers }>,
): Promise<WriteResult<T>> {
  const r = await call();
  return {
    entity: r.data,
    pending: r.headers.get('X-Change-Request-Status') === 'pending',
    changeRequestId: r.headers.get('X-Change-Request-Id'),
  };
}

export const agreementsApi = {
  list:    (p: AgreementsListParams = {})    => api.get<Page<Agreement>>(`/agreements${qs(p)}`),
  get:     (id: UUID, includeDeleted = false) =>
    api.get<Agreement>(`/agreements/${id}${includeDeleted ? '?include_deleted=true' : ''}`),
  create:  (payload: AgreementCreateInput)   => writeWithMaker(() => api.postMeta<Agreement>('/agreements', payload)),
  update:  (id: UUID, p: AgreementUpdateInput) => writeWithMaker(() => api.patchMeta<Agreement>(`/agreements/${id}`, p)),
  delete:  (id: UUID)                        => writeWithMaker(() => api.deleteMeta<Agreement>(`/agreements/${id}`)),
  restore: (id: UUID)                        => writeWithMaker(() => api.postMeta<Agreement>(`/agreements/${id}/restore`)),
  nextClientNumber: (service_type: ServiceType) =>
    api.get<{ next: number }>(`/agreements/next-client-number?service_type=${service_type}`),
  suggestMnemonic: (first: string, last: string) =>
    api.get<{ mnemonic: string; base: string; was_modified: boolean }>(
      `/agreements/suggest-mnemonic${qs({ first, last })}`,
    ),
  /** P10: the agreement's parties with the MiFIR NCI / LEI each would give. */
  mifirSuggestions: (id: UUID, idType: 'NCI' | 'LEI') =>
    api.get<MifirSuggestions>(`/agreements/${id}/mifir-suggestions${qs({ id_type: idType })}`),
  checkMnemonic: (candidate: string) =>
    api.get<{ available: boolean; conflicting_code_1: string | null }>(
      `/agreements/check-mnemonic${qs({ candidate })}`,
    ),
  references:    (id: UUID) => api.get<ReferencesReport>(`/agreements/${id}/references`),
  cascadeDelete: (id: UUID, refIds: Record<string, UUID[]>) =>
    writeWithMaker(() => api.postMeta<Agreement>(`/agreements/${id}/cascade-delete`, { ref_ids: refIds })),

  // #262 cascade-Inactivo / cascade-Closed (2026-06-08). LifecycleStatus
  // is the canonical shared type, defined in api/types.ts (#273 sync).
  cascadeFootprint: (id: UUID, toStatus: LifecycleStatus) =>
    api.get<CascadeFootprint>(
      `/agreements/${id}/cascade-footprint?to_status=${encodeURIComponent(toStatus)}`,
    ),
  cascadeStatusApply: (
    id: UUID,
    body: { to_status: LifecycleStatus; cascade_plan: CascadePlan },
  ) =>
    api.post<CascadeStatusResponse>(`/agreements/${id}/cascade-status`, body),

  // #51: promote a Prospect → Active (assigns the number, flips status) via
  // maker-checker. Compliance/Admin auto-apply; RM filings come back pending.
  promote: (id: UUID) =>
    writeWithMaker(() => api.postMeta<Agreement>(`/agreements/${id}/promote`, {})),

  // Phase 3 (C-166): faithful reactivate. The footprint lists the
  // cascade-touched children that will be restored; the apply restores exactly
  // those and reopens the agreement to its reason-driven target (LOST_LEAD →
  // Prospect, else Active). Same plan shape as cascade — build via footprintToPlan().
  reactivateFootprint: (id: UUID) =>
    api.get<CascadeFootprint>(`/agreements/${id}/reactivate-footprint`),
  reactivate: (id: UUID, body: { reactivate_plan: CascadePlan }) =>
    api.post<CascadeStatusResponse>(`/agreements/${id}/reactivate`, body),
};

// ── #262 cascade-lifecycle types (matches backend) ───────────────────────
// #273 (2026-06-08): LifecycleStatus moved to api/types.ts as the canonical
// shared type. Re-exported at the top of this file for legacy callers.

export interface CascadeFootprintItem {
  id: UUID;
  label: string;
  current_status: LifecycleStatus;
  via_policy_id?: UUID | null;
  via_policy_label?: string | null;
}

export interface CascadeFootprint {
  agreement_id: UUID;
  from_status: LifecycleStatus;
  to_status: LifecycleStatus;
  direct_accounts: CascadeFootprintItem[];
  policies: CascadeFootprintItem[];
  indirect_accounts: CascadeFootprintItem[];
  summary: { direct_accounts: number; policies: number; indirect_accounts: number };
}

/** Plan_dict shape passed to POST /cascade-status. Build via footprintToPlan(). */
export interface CascadePlan {
  agreement_id: UUID;
  from_status: LifecycleStatus;
  to_status: LifecycleStatus;
  direct_account_ids: UUID[];
  policy_ids: UUID[];
  indirect_account_ids: UUID[];
  summary: { direct_accounts: number; policies: number; indirect_accounts: number };
}

export function footprintToPlan(fp: CascadeFootprint): CascadePlan {
  return {
    agreement_id: fp.agreement_id,
    from_status: fp.from_status,
    to_status: fp.to_status,
    direct_account_ids: fp.direct_accounts.map((it) => it.id),
    policy_ids: fp.policies.map((it) => it.id),
    indirect_account_ids: fp.indirect_accounts.map((it) => it.id),
    summary: fp.summary,
  };
}

export interface CascadeStatusResponse {
  agreement_id: UUID;
  new_status: LifecycleStatus;
  affected_direct_account_count: number;
  affected_policy_count: number;
  affected_indirect_account_count: number;
  change_request_id: UUID | null;
  change_request_status: string | null;
}

/** Mirrors backend `is_cascade_triggering`. Cascade-triggering iff target
 *  is more terminal than source AND not equal AND not Active. */
export function isCascadeTriggering(
  from: LifecycleStatus,
  to: LifecycleStatus,
): boolean {
  if (from === to) return false;
  if (to === 'Active') return false;
  // #51: nothing transitions INTO Prospect; Prospect→Active is promotion (its
  // own flow, already handled by the to==='Active' guard). Prospect→Closed
  // (close-lost) cascades, via the ranking below (Prospect < Active).
  if (to === 'Prospect') return false;
  const order: Record<LifecycleStatus, number> = {
    Prospect: -1,
    Active: 0,
    Inactive: 1,
    Closed: 2,
  };
  return order[to] > order[from];
}
